import { devWarn, devError, devInfo } from "@/lib/logger";
/**
 * Web Push subscription helper.
 * - Ensures the service worker is ready.
 * - Fetches VAPID public key from server.
 * - Manages Notification permission + PushManager subscription.
 * - Uploads / removes subscription from the backend.
 */
import { getVapidPublicKey, pushSubscribe, pushUnsubscribe } from "@/lib/api";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const arrayBufferToBase64 = (buf) => {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const currentPermission = () => (typeof Notification !== "undefined" ? Notification.permission : "denied");

export const getCurrentSubscription = async () => {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
};

export const enablePush = async () => {
  if (!pushSupported()) throw new Error("This browser does not support push notifications.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied.");

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    // Already subscribed — just re-sync with server
    await pushSubscribe(subscriptionToPayload(existing));
    return existing;
  }

  const { public_key } = await getVapidPublicKey();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(public_key),
  });
  await pushSubscribe(subscriptionToPayload(sub));
  return sub;
};

export const disablePush = async () => {
  const sub = await getCurrentSubscription();
  if (!sub) return { removed: false };
  try {
    await pushUnsubscribe(sub.endpoint);
  } catch (err) {
    // Server may already have pruned this subscription — non-fatal, but log for visibility.
    devWarn("[push] server-side unsubscribe skipped:", err?.message || err);
  }
  await sub.unsubscribe();
  return { removed: true };
};

const subscriptionToPayload = (sub) => {
  const j = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    p256dh: j.keys.p256dh,
    auth: j.keys.auth,
    user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
  };
};
